import { useCallback, useMemo, useState } from 'react';
import { useWorkspaceTime } from './useWorkspaceTime';
import {
  CsvImportedDataRow,
  CsvImportedPost,
  ErrorDetail,
  ErrorState,
  ImportResults,
  ValidationReturnType
} from '../models/csv-importer';
import { CsvColumnNames, CsvHeader, ValidationErrorTypeEnum } from '../constants/csv-importer';
import { CoreCategoryFieldsFragment } from '../models/graphql';
import { imagesStringToStringArray, mapCsvImportedDataToPost } from '../helpers/csvImporterHelpers';
import { MaxCsvFileSize, MaxImagesPerPostsForImport, MaxSelectedPostsToImport } from '../constants/find-content';
import { PhotoExtensions } from '../constants/media';
import { ValidationPatterns } from '../constants/form';
import { isValidUrl } from '../helpers/urlHelpers';
import { DateTimeStringFormatWithoutSeconds } from '../constants/time-formats';
import { isValidDateTimeWithoutSecondsString } from '../helpers/timeFormatter';
import { isPast, isValid } from 'date-fns';

export const useCsvImporterValidator = () => {
  const { parseDateInWorkspaceTime } = useWorkspaceTime();
  const emptyErrorState: ErrorState = useMemo(
    () => ({
      errorMessages: {},
      errorDetails: {}
    }),
    []
  );
  const [errorState, setErrorState] = useState<ErrorState>(emptyErrorState);

  // @TODO: we should accept either a ErrorDetail || ErrorDetail[]
  const addErrors = (errors: ErrorDetail[]) => {
    setErrorState(prevState => {
      const updatedErrorMessages = { ...prevState.errorMessages };
      const updatedValidationErrorDetails = { ...prevState.errorDetails };

      errors.forEach(({ message, row, column, type }) => {
        if (type === ValidationErrorTypeEnum.FILE) {
          // Handle file errors without row, column, or error details
          if (updatedErrorMessages[message]) {
            updatedErrorMessages[message].push(-1); // Using -1 to indicate file-level error
          } else {
            updatedErrorMessages[message] = [-1];
          }
        } else if (type === ValidationErrorTypeEnum.DATA) {
          // Update error messages for data errors
          if (updatedErrorMessages[message]) {
            updatedErrorMessages[message].push(row);
          } else {
            updatedErrorMessages[message] = [row];
          }

          // Update the validation error details at the row/column level
          if (column !== undefined) {
            if (!updatedValidationErrorDetails[row]) {
              updatedValidationErrorDetails[row] = {} as { [key in CsvColumnNames]?: boolean };
            }
            updatedValidationErrorDetails[row][column] = true;
          }
        }
      });

      return {
        errorMessages: updatedErrorMessages,
        errorDetails: updatedValidationErrorDetails
      };
    });
  };

  const validateData = useCallback(
    (results: ImportResults, categories: CoreCategoryFieldsFragment[]): ValidationReturnType => {
      let posts: CsvImportedPost[] = [];
      let errors: ErrorDetail[] = [];

      const hasHeader = hasCsvHeader(results.data[0]);
      // If the Headers are not valid or if no posts data rows, we do not continue

      const rawData: CsvImportedDataRow[] = hasHeader ? results.data.slice(1) : results.data;

      // We filter out empty rows to ensure we only process data
      const postData = rawData.filter(row => row.length > 0 && row.some(c => c !== undefined && c.trim() !== ''));

      try {
        const postCountError = validatePostCountLimit(postData);
        if (postCountError) {
          throw new Error(postCountError.message);
        }

        // Since we will show posts for the below errors, we set posts now
        posts = mapCsvImportedDataToPost(postData);

        // If no post data, then the import is empty
        if (!postData?.length) {
          throw new Error('No post were detected for import');
        }

        // const columnValidationError = validateColumnSizes(postData);
        // if (columnValidationError) {
        //   throw new Error(columnValidationError.message);
        // }

        errors = [
          ...validateCategory(posts, categories),
          ...validateEvergreen(posts),
          ...validateSchedule(posts, parseDateInWorkspaceTime),
          ...validateImages(posts),
          ...validateLink(posts)
        ];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } catch (e: Error) {
        errors = [{ message: e.message, row: 0, type: ValidationErrorTypeEnum.DATA }];
      }

      // Any errors that occurred are now added to the state
      addErrors(errors);

      return {
        hasErrors: !!errors.length,
        posts
      };
    },
    [parseDateInWorkspaceTime]
  );

  const validateFile = useCallback(
    (file: File) => {
      // Always clear all errors when validating a new file
      setErrorState(emptyErrorState);

      const errors = validateImportedCsvFile(file);

      if (errors.length) {
        addErrors(errors);
      }
    },
    [emptyErrorState]
  );

  return useMemo(
    () => ({
      validateData,
      validateFile,
      errorState
    }),
    [errorState, validateData, validateFile]
  );
};

const validateImportedCsvFile = (file: File) => {
  const errors: ErrorDetail[] = [];

  if (!file.name.endsWith('.csv')) {
    errors.push({
      message: `${file.name} is not a valid .csv file`,
      row: 0,
      type: ValidationErrorTypeEnum.FILE
    });
  }

  if (Number(file.size) > MaxCsvFileSize) {
    errors.push({
      message: `${file.name} is too large, maximum file size is 15.0MB`,
      row: 0,
      type: ValidationErrorTypeEnum.FILE
    });
  }

  return errors;
};

// We have a limit on the number of posts to be imported at one time.
const validatePostCountLimit = (postData: CsvImportedDataRow[]): ErrorDetail | undefined => {
  if (postData.length > MaxSelectedPostsToImport) {
    const errorMessage = `You are attempting to import ${postData.length} posts, which exceeds the maximum allowed limit of ${MaxSelectedPostsToImport}.`;
    // const errorOptions: ErrorOptions = { cause: 'IMPORT_COUNT_EXCEEDED' };
    // addErrors([{ message: errorMessage, row: 0, type: ValidationErrorTypeEnum.DATA }]);

    return { message: errorMessage, row: 0, type: ValidationErrorTypeEnum.DATA };
  }

  return;
};

// const validateColumnSizes = (postData: CsvImportedDataRow[]): ErrorDetail | undefined => {
//   const hasValidColumnCounts = postData.every(row => row.length >= 6);
//
//   if (!hasValidColumnCounts) {
//     return { message: 'Some rows are missing columns', row: 0, type: ValidationErrorTypeEnum.DATA };
//   }
//
//   return;
// };

const validateCategory = (posts: CsvImportedPost[], categories: CoreCategoryFieldsFragment[]): ErrorDetail[] => {
  const errors: ErrorDetail[] = [];
  const categoryNames = categories?.map(category => category.name) || [];

  posts.forEach((post, index): void => {
    const categoryName = post.category.trim();
    if (categoryName === '') {
      // If category is an empty string or only whitespace, it's valid
      return;
    }

    if (!categoryNames.includes(categoryName)) {
      errors.push({
        type: ValidationErrorTypeEnum.DATA,
        message: 'Invalid category name',
        row: index,
        column: CsvColumnNames.CATEGORY
      });
    }
  });

  return errors;
};

// Images can be a single string, or a quoted grouping delimited with a coma within the quotes?
// Images must be in JPG/PNG/GIF/WEBP format
const validateImages = (posts: CsvImportedPost[]): ErrorDetail[] => {
  const errors: ErrorDetail[] = [];
  const extensionPattern = new RegExp(`\\.(${Object.values(PhotoExtensions).join('|')})$`, 'i');

  posts.forEach((post, index): void => {
    const images = imagesStringToStringArray(post.images);

    let imageCount = 0;

    if (post.images.trim() === '') {
      // If link is an empty string or only whitespace, it's valid
      return;
    }

    images.forEach((image: string) => {
      const trimmedImage = image.trim();

      if (trimmedImage === '') return;

      // We do here to prevent the counting of empty strings
      imageCount++;

      if (!ValidationPatterns.url.test(trimmedImage)) {
        errors.push({
          type: ValidationErrorTypeEnum.DATA,
          message: 'Invalid Image URL',
          row: index,
          column: CsvColumnNames.IMAGES
        });
      } else if (!extensionPattern.test(trimmedImage)) {
        errors.push({
          type: ValidationErrorTypeEnum.DATA,
          message: 'Images must be in JPG/PNG/GIF/WEBP format',
          row: index,
          column: CsvColumnNames.IMAGES
        });
      }
    });

    // Once all has been done, we should have a legit count of actual images
    if (imageCount > MaxImagesPerPostsForImport) {
      errors.push({
        type: ValidationErrorTypeEnum.DATA,
        message: `Number of images on post has exceed the maximum of ${MaxImagesPerPostsForImport}`,
        row: index,
        column: CsvColumnNames.IMAGES
      });
    }
  });

  return errors;
};

// Must be either empty, or the value of "Once" or "Evergreen"
const validateEvergreen = (posts: CsvImportedPost[]): ErrorDetail[] => {
  const errors: ErrorDetail[] = [];
  posts.forEach((post, index): void => {
    const evergreenValue = post.evergreen.toLowerCase().trim();
    if (evergreenValue && evergreenValue !== 'once' && evergreenValue !== 'evergreen') {
      errors.push({
        type: ValidationErrorTypeEnum.DATA,
        message: 'Invalid evergreen value',
        row: index,
        column: CsvColumnNames.EVERGREEN
      });
    }
  });

  return errors;
};

const validateLink = (posts: CsvImportedPost[]): ErrorDetail[] => {
  const errors: ErrorDetail[] = [];

  posts.forEach((post, index) => {
    if (!isValidUrl(post.link.trim(), true)) {
      errors.push({
        type: ValidationErrorTypeEnum.DATA,
        message: `Some Links are invalid`,
        row: index,
        column: CsvColumnNames.LINK
      });
    }
  });

  return errors;
};

// Is time format valid?
// Is time in the future?
// Is time in increments of 5 minutes?
const validateSchedule = (
  posts: CsvImportedPost[],
  parseDateInWorkspaceTime: (dateString: string, formatString: string) => Date
): ErrorDetail[] => {
  const errors: ErrorDetail[] = [];

  posts.forEach((post, index): void => {
    const scheduledTime = post.scheduledTime.trim();

    const date = parseDateInWorkspaceTime(scheduledTime, DateTimeStringFormatWithoutSeconds);
    if (scheduledTime === '') {
      // If scheduledTime is an empty string or only whitespace, it's valid
      return;
    }

    if (!isValidDateTimeWithoutSecondsString(scheduledTime) || !isValid(date)) {
      errors.push({
        type: ValidationErrorTypeEnum.DATA,
        message: `Make sure that all dates are in the future, in the format '${DateTimeStringFormatWithoutSeconds}'`,
        row: index,
        column: CsvColumnNames.SCHEDULED_TIME
      });
      return;
    }

    // const zonedDate = convertUtcTimeToWorkspaceTime(date);

    if (isPast(date)) {
      errors.push({
        type: ValidationErrorTypeEnum.DATA,
        message: 'Date must be in the future.',
        row: index,
        column: CsvColumnNames.SCHEDULED_TIME
      });
    }

    const minutes = date.getMinutes();
    if (minutes % 5 !== 0) {
      errors.push({
        type: ValidationErrorTypeEnum.DATA,
        message: 'Date must be rounded to the nearest 5 minutes.',
        row: index,
        column: CsvColumnNames.SCHEDULED_TIME
      });
    }
  });

  return errors;
};

const hasCsvHeader = (headerRow: CsvImportedDataRow): boolean => {
  // Simple exact match check
  if (headerRow && JSON.stringify(headerRow).toLowerCase() === JSON.stringify(CsvHeader).toLowerCase()) {
    return true;
  }

  // Check for date column if we do not match exactly
  try {
    if (headerRow[3].toLowerCase() === CsvHeader[3].toLowerCase()) return true;
  } catch (e) {
    return false;
  }

  // Change to lower case to do a case independent comparison
  const lowerCaseCsvHeader = CsvHeader.map(header => header.toLowerCase());
  const lowerCaseImportedHeader = headerRow.map(header => (header ? header.toLowerCase() : ''));

  // Check if there is atleast one intended header column name
  return lowerCaseCsvHeader.some(actualHeader => {
    return !!(actualHeader && lowerCaseImportedHeader.includes(actualHeader));
  });
};
